import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  Button,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  TableToolbar,
  TableToolbarActions,
  RefreshIcon,
  TablePagination,
  CloseIcon,
  CheckIcon,
  FileIcon,
  Amount,
  useToast,
} from '@razorpay/blade/components';
import { formatPaginationRange } from './utils';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import pluralize from 'pluralize';
import { useMutation, useQueryClient } from 'react-query';
import { routePaths } from 'components/Routes/data';
import { useHistory, useLocation } from 'react-router-dom';
import { ClaimDetailDrawer } from './ClaimDetailDrawer';
import { approveOrRejectFlexiReiumbersementClaim } from 'api/gql-utils/flexibleBenefits/mutations/approveFlexiReiumbersementClaim';
import { BulkActionModal } from './BulkActionModal';
import {
  ApproveOrRejectFlexiReiumbersementClaimInput,
  FlexiReimbursementClaim,
} from 'api/gql-utils/flexibleBenefits/types';
import { bulkUpdateFlexiReimbursementClaimsStatus } from 'api/gql-utils/flexibleBenefits/mutations/bulkUpdateFlexiReiumbersementClaimStatus';
import { convertPaiseToRupees } from 'components/SalaryComponents/views/FlexibleBenefits/Claims/Edit/util';
import { DecisionType } from './types';

interface PendingApprovalProps {
  claimRequests: FlexiReimbursementClaim[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  totalCount: number;
}

export const PendingApproval = ({
  claimRequests,
  isLoading,
  isFetching,
  refetch,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  totalCount,
}: PendingApprovalProps) => {
  const [selectedRequests, setSelectedRequests] = useState<Record<string, FlexiReimbursementClaim>>(
    {},
  );
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const searchParams = new URLSearchParams(location.search);
  const claimIdFromUrl = searchParams.get('claimId');
  const queryClient = useQueryClient();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [initialDecision, setInitialDecision] = useState<DecisionType>('approve');

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<DecisionType>('approve');

  const approveFlexiReiumbersementClaimMutation = useMutation({
    mutationFn: (input: ApproveOrRejectFlexiReiumbersementClaimInput) =>
      approveOrRejectFlexiReiumbersementClaim({
        claim_id: input.claim_id,
        status: input.status,
        approved_amount: input.approved_amount,
        review_comments: input.review_comments,
      }),
    onSuccess: (data) => {
      toast.show({
        content: `Claim approved successfully`,
        color: 'positive',
        duration: 3000,
      });
      handleCloseDrawer();
      refetch();
    },
    onError: () => {
      toast.show({
        content: 'Failed to approve claim, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const rejectFlexiReiumbersementClaimMutation = useMutation({
    mutationFn: (input: ApproveOrRejectFlexiReiumbersementClaimInput) =>
      approveOrRejectFlexiReiumbersementClaim({
        claim_id: input.claim_id,
        status: input.status,
        approved_amount: input.approved_amount,
        review_comments: input.review_comments,
      }),
    onSuccess: (data) => {
      toast.show({
        content: `Claim rejected successfully`,
        color: 'positive',
        duration: 3000,
      });
      handleCloseDrawer();
      refetch();
    },
    onError: () => {
      toast.show({
        content: 'Failed to reject claim, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: bulkUpdateFlexiReimbursementClaimsStatus,
    onSuccess: (data) => {
      const response = data?.payrollFlexiReimbursementClaimsBulkApproveOrReject;

      const successCount = response?.claims?.length || 0;
      toast.show({
        content: `${successCount} ${pluralize('claim', successCount)} approved successfully`,
        color: 'positive',
        duration: 3000,
      });

      setIsBulkModalOpen(false);
      setSelectedRequests({});
      queryClient.invalidateQueries(['flexi-reimbursement-claims-list']);
    },
    onError: () => {
      toast.show({
        content: 'Failed to approve claims, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const bulkRejectMutation = useMutation({
    mutationFn: bulkUpdateFlexiReimbursementClaimsStatus,
    onSuccess: (data) => {
      const response = data?.payrollFlexiReimbursementClaimsBulkApproveOrReject;
      const successCount = response?.claims?.length || 0;
      toast.show({
        content: `${successCount} ${pluralize('claim', successCount)} rejected successfully`,
        color: 'positive',
        duration: 3000,
      });
      setIsBulkModalOpen(false);
      setSelectedRequests({});
      queryClient.invalidateQueries(['flexi-reimbursement-claims-list']);
    },
    onError: () => {
      toast.show({
        content: 'Failed to reject claims, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  useEffect(() => {
    if (claimIdFromUrl) {
      setSelectedClaimId(claimIdFromUrl);
      setIsDrawerOpen(true);
    }
  }, [claimIdFromUrl]);

  const handleOpenDrawer = (claimId: string, decision: DecisionType) => {
    setSelectedClaimId(claimId);
    setInitialDecision(decision);
    setIsDrawerOpen(true);
    history.push(
      `${routePaths.approvalWorkflows.myRequests.flexiReimbursement.pendingApprovals}?claimId=${claimId}`,
    );
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedClaimId(null);
    history.push(routePaths.approvalWorkflows.myRequests.flexiReimbursement.pendingApprovals);
  };

  const selectedRowsCount = Object.keys(selectedRequests).length;

  const selectedClaimsForModal: FlexiReimbursementClaim[] = useMemo(() => {
    return Object.values(selectedRequests);
  }, [selectedRequests]);

  const handleBulkApprove = () => {
    setBulkActionType('approve');
    setIsBulkModalOpen(true);
  };

  const handleBulkReject = () => {
    setBulkActionType('reject');
    setIsBulkModalOpen(true);
  };

  const handleBulkConfirm = (data: {
    claims: Array<{
      claimId: string;
      approvedAmount?: number;
      reviewComments?: string;
    }>;
    commonComment: string;
  }) => {
    if (bulkActionType === 'approve') {
      bulkApproveMutation.mutate({
        claim_ids: data.claims.map((claim) => claim.claimId),
        status: 'APPROVED',
        review_comments: data.commonComment,
      });
    } else {
      bulkRejectMutation.mutate({
        claim_ids: data.claims.map((claim) => claim.claimId),
        review_comments: data.commonComment,
        status: 'REJECTED',
      });
    }
  };

  return (
    <Box marginTop="spacing.6">
      <Table
        isLoading={isLoading}
        gridTemplateColumns={
          claimRequests && claimRequests.length > 0 ? '68px 1.5fr 1fr 1fr 1fr 1.5fr 1fr' : undefined
        }
        key={claimRequests?.length}
        isRefreshing={isFetching}
        multiSelectTrigger="checkbox"
        selectionType="multiple"
        onSelectionChange={(props) => {
          const data = props.selectedIds.reduce(
            (acc: Record<string, FlexiReimbursementClaim>, id) => {
              const idString = String(id);
              const item = claimRequests?.find((item) => item.claim_id === idString);
              if (item) {
                acc[idString] = item;
              }
              return acc;
            },
            {} as Record<string, FlexiReimbursementClaim>,
          );
          setSelectedRequests(data);
        }}
        data={{
          nodes:
            claimRequests?.map((item) => ({
              id: item.claim_id,
              ...item,
            })) || [],
        }}
        pagination={
          claimRequests && claimRequests.length > 0 ? (
            <TablePagination
              currentPage={currentPage}
              paginationType="server"
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
              totalItemCount={totalCount}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar
            selectedTitle={`${selectedRowsCount} ${pluralize(
              'Request',
              selectedRowsCount,
            )} Selected`}
            title={
              claimRequests && claimRequests.length > 0
                ? `Showing ${formatPaginationRange({
                    totalItems: totalCount,
                    currentPage,
                    itemsPerPage,
                  })} Pending Approvals`
                : ''
            }>
            <TableToolbarActions>
              <Box display="flex" gap="spacing.5">
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Pending Requests"
                  onClick={() => refetch()}
                  isDisabled={isFetching}
                />
                {selectedRowsCount > 0 && (
                  <Button
                    color="negative"
                    icon={CloseIcon}
                    variant="secondary"
                    isDisabled={isFetching}
                    onClick={handleBulkReject}>
                    Reject
                  </Button>
                )}
                {selectedRowsCount > 0 && (
                  <Button icon={CheckIcon} isDisabled={isFetching} onClick={handleBulkApprove}>
                    Approve
                  </Button>
                )}
              </Box>
            </TableToolbarActions>
          </TableToolbar>
        }>
        {(nodes) =>
          claimRequests && claimRequests.length > 0 ? (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Name
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Benefit type
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Amount
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Proof uploaded
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Description
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Expense date
                    </Text>
                  </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {nodes.map((item) => (
                  <TableRow
                    key={item.id}
                    item={item}
                    hoverActions={
                      <Box display="flex" gap="spacing.4" alignItems={'center'}>
                        <Button
                          size="xsmall"
                          onClick={() => {
                            handleOpenDrawer(item.id, 'approve');
                          }}>
                          View Details
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap="spacing.1">
                        <Text size="medium" weight="regular">
                          {item.employee_name}
                        </Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {item.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text size="medium" weight="regular">
                        {item.component_display_name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Amount value={Number(convertPaiseToRupees(item.claim_amount))} />
                    </TableCell>
                    <TableCell>
                      <Badge icon={FileIcon} color="information">
                        {item.claim_evidence_id.length}{' '}
                        {pluralize('attachment', item.claim_evidence_id.length)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text
                        size="medium"
                        weight="regular"
                        truncateAfterLines={1}
                        color="surface.text.gray.muted">
                        {item.claim_description || '--'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text size="medium" weight="regular">
                        {item.expense_date
                          ? getFormattedDateValue({
                              date: new Date(
                                Number(item.expense_date) > 10000000000
                                  ? Number(item.expense_date)
                                  : Number(item.expense_date) * 1000,
                              ),
                              formatString: dateFormats.dateShortMonthCommaYear,
                            })
                          : '--'}
                      </Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <EmptyView
              title="You're all caught up!"
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  There are no claims pending for your approval
                </Text>
              }
            />
          )
        }
      </Table>

      <ClaimDetailDrawer
        isOpen={isDrawerOpen}
        onDismiss={handleCloseDrawer}
        claimId={selectedClaimId}
        initialDecision={initialDecision}
        onStatusChange={(data) => {
          if (data.status === 'APPROVED') {
            approveFlexiReiumbersementClaimMutation.mutate({
              claim_id: data.claimId,
              status: 'APPROVED',
              approved_amount: data.approvedAmount,
              review_comments: data.review_comments,
            });
          } else {
            rejectFlexiReiumbersementClaimMutation.mutate({
              claim_id: data.claimId,
              status: 'REJECTED',
              review_comments: data.review_comments,
            });
          }
        }}
      />

      <BulkActionModal
        isOpen={isBulkModalOpen}
        onDismiss={() => setIsBulkModalOpen(false)}
        actionType={bulkActionType}
        selectedClaims={selectedClaimsForModal}
        onConfirm={handleBulkConfirm}
        isLoading={bulkApproveMutation.isLoading || bulkRejectMutation.isLoading}
      />
    </Box>
  );
};
